import { useState } from "react";
import { useGame, MODES, WORLDS, TRACKS } from "./store";
import { initAudio } from "./sound";

export default function StartMenu() {
  const world = useGame((s) => s.world);
  const track = useGame((s) => s.track);
  const setWorld = useGame((s) => s.setWorld);
  const setTrack = useGame((s) => s.setTrack);
  const setPlayerName = useGame((s) => s.setPlayerName);
  const startGame = useGame((s) => s.startGame);
  const playerName = useGame((s) => s.playerName);
  const examUnlocked = useGame((s) => s.examUnlocked);
  const challengeUnlocked = useGame((s) => s.challengeUnlocked);
  const openExam = useGame((s) => s.openExam);
  const openLab = useGame((s) => s.openLab);
  const openChallengeSelect = useGame((s) => s.openChallengeSelect);
  const openMap = useGame((s) => s.openMap);

  return (
    <div
      data-testid="start-menu"
      style={{
        position: "absolute",
        inset: 0,
        background:
          "radial-gradient(ellipse at top, #1a1d3a 0%, #07080f 60%, #02030a 100%)",
        color: "#fff",
        fontFamily: "'Inter', system-ui, sans-serif",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        overflowY: "auto",
      }}
    >
      {/* Brand */}
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        <div
          style={{
            fontSize: 13,
            letterSpacing: 6,
            color: "#e879f9",
            fontWeight: 600,
          }}
        >
          PRAYOGASHAALA
        </div>
        <h1
          style={{
            fontSize: 56,
            margin: "8px 0 4px",
            fontWeight: 900,
            background:
              "linear-gradient(90deg, #ffd23f 0%, #ff4d6d 50%, #e879f9 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: -2,
          }}
        >
          Physics Coaster
        </h1>
        <div style={{ color: "#9aa4b2", fontSize: 15, maxWidth: 540 }}>
          Welcome to Prayogashaala Physics Park. Pick any module that matches your syllabus —
          ride it, certify the physics, earn your Engineer's License stamp.
        </div>
      </div>

      {/* Player name */}
      <div style={{ width: "min(420px, 100%)", marginBottom: 24 }}>
        <label
          style={{
            fontSize: 11,
            letterSpacing: 2,
            color: "#9aa4b2",
            display: "block",
            marginBottom: 6,
          }}
        >
          ANONYMOUS PILOT NAME
        </label>
        <input
          data-testid="input-player-name"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          style={{
            width: "100%",
            padding: "12px 14px",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 10,
            color: "#fff",
            fontSize: 16,
            outline: "none",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        />
      </div>

      {/* Track selector — two large visual cards */}
      <div style={{ width: "min(680px, 100%)", marginBottom: 30 }}>
        <div style={{ fontSize: 11, letterSpacing: 2, color: "#9aa4b2", marginBottom: 12 }}>
          CHOOSE YOUR TRACK
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {Object.entries(TRACKS).map(([key, t]) => {
            const selected = track === key;
            return (
              <button
                key={key}
                data-testid={`track-${key.toLowerCase()}`}
                onClick={() => setTrack(key)}
                style={{
                  padding: "20px 18px",
                  background: selected
                    ? `linear-gradient(145deg, ${t.color}22, ${t.color}08)`
                    : "rgba(255,255,255,0.03)",
                  border: selected
                    ? `2px solid ${t.color}`
                    : "2px solid rgba(255,255,255,0.08)",
                  borderRadius: 16,
                  color: "#fff",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "border-color 0.15s, background 0.15s",
                  position: "relative",
                  boxShadow: selected ? `0 0 24px ${t.color}33` : "none",
                }}
              >
                {selected && (
                  <div style={{
                    position: "absolute", top: 10, right: 12,
                    fontSize: 11, fontWeight: 800, letterSpacing: 2,
                    color: t.color, opacity: 0.9,
                  }}>✓ SELECTED</div>
                )}
                <div style={{ fontSize: 30, marginBottom: 6 }}>{t.emoji}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: selected ? t.color : "#e8ecf3" }}>
                  {t.name}
                </div>
                <div style={{ fontSize: 11.5, color: "#9aa4b2", marginTop: 3, marginBottom: 10 }}>
                  {t.subtitle}
                </div>
                <div style={{ fontSize: 12.5, color: "#cfd6e3", lineHeight: 1.6 }}>
                  {t.description}
                </div>
                <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontFamily: "monospace", padding: "3px 8px",
                    background: "rgba(255,255,255,0.07)", borderRadius: 6, color: "#9aa4b2" }}>
                    μ = {t.mu}
                  </span>
                  <span style={{ fontSize: 11, fontFamily: "monospace", padding: "3px 8px",
                    background: "rgba(255,255,255,0.07)", borderRadius: 6, color: "#9aa4b2" }}>
                    air drag ×{t.airMult}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Start button */}
      <button
        data-testid="start-game-btn"
        onClick={() => {
          initAudio();
          startGame();
        }}
        style={{
          padding: "18px 56px",
          background: "linear-gradient(135deg, #00ffaa 0%, #00b8a9 100%)",
          color: "#001a14",
          border: "none",
          borderRadius: 14,
          fontSize: 20,
          fontWeight: 800,
          cursor: "pointer",
          letterSpacing: 2,
          boxShadow: "0 12px 40px rgba(0,255,170,0.35)",
          transition: "transform 0.15s",
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        ▶ START RACE
      </button>

      {/* Park Map — the hub of the Physics Park */}
      <button
        data-testid="menu-map-btn"
        onClick={openMap}
        style={{
          marginTop: 14,
          padding: "14px 44px",
          background: "linear-gradient(135deg, #ffd23f 0%, #ff8a3d 100%)",
          color: "#1a0d00",
          border: "none",
          borderRadius: 14,
          fontSize: 16,
          fontWeight: 800,
          cursor: "pointer",
          letterSpacing: 1.5,
        }}
      >
        🗺 PARK MAP — phases, license & progress
      </button>

      {/* Mode shelf: unlockable Exam / Lab / Challenge */}
      <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap", justifyContent: "center" }}>
        <button
          data-testid="menu-exam-btn"
          onClick={() => examUnlocked && openExam()}
          disabled={!examUnlocked}
          style={{
            padding: "12px 22px",
            background: examUnlocked
              ? "linear-gradient(135deg, #3eb6ff, #e879f9)"
              : "rgba(255,255,255,0.05)",
            border: "none",
            borderRadius: 10,
            color: examUnlocked ? "#0a0c1e" : "#5a6478",
            fontWeight: 800,
            fontSize: 14,
            cursor: examUnlocked ? "pointer" : "not-allowed",
          }}
        >
          📝 EXAM MODE {examUnlocked ? "" : "🔒 finish 1 lap"}
        </button>
        <button
          data-testid="menu-lab-btn"
          onClick={() => examUnlocked && openLab(null)}
          disabled={!examUnlocked}
          style={{
            padding: "12px 22px",
            background: examUnlocked ? "rgba(62,182,255,0.15)" : "rgba(255,255,255,0.05)",
            border: examUnlocked ? "1px solid rgba(62,182,255,0.5)" : "1px solid rgba(255,255,255,0.1)",
            borderRadius: 10,
            color: examUnlocked ? "#3eb6ff" : "#5a6478",
            fontWeight: 800,
            fontSize: 14,
            cursor: examUnlocked ? "pointer" : "not-allowed",
          }}
        >
          🔬 PHYSICS LAB {examUnlocked ? "" : "🔒"}
        </button>
        <button
          data-testid="menu-challenge-btn"
          onClick={() => challengeUnlocked && openChallengeSelect()}
          disabled={!challengeUnlocked}
          style={{
            padding: "12px 22px",
            background: challengeUnlocked
              ? "linear-gradient(135deg, #ffd23f, #ff8a3d)"
              : "rgba(255,255,255,0.05)",
            border: challengeUnlocked ? "none" : "1px solid rgba(255,255,255,0.1)",
            borderRadius: 10,
            color: challengeUnlocked ? "#1a0d00" : "#5a6478",
            fontWeight: 800,
            fontSize: 14,
            cursor: challengeUnlocked ? "pointer" : "not-allowed",
          }}
        >
          🏁 CHALLENGE
        </button>
      </div>

      <div
        style={{
          marginTop: 24,
          fontSize: 12,
          color: "#5a6478",
          textAlign: "center",
          maxWidth: 540,
          lineHeight: 1.6,
        }}
      >
        Tip: Use <b style={{ color: "#00ffaa" }}>↑ Arrow</b> to accelerate and{" "}
        <b style={{ color: "#ff4d6d" }}>↓ Arrow</b> to brake.
        <br />
        At every curve, solve for <b style={{ color: "#ffd23f" }}>v_max</b> first — the grip meter
        shows how close you are to the physical limit. Exceed it and you derail!
        Off the track? Press <b style={{ color: "#00ffaa" }}>C</b> to climb back.
      </div>
    </div>
  );
}
